import axios from 'axios';

// ──────────────────────────────────────────────────────────────
//  Labels store — now backed by the new designer system (/api/designer).
//
//  The rest of the app was written against the OLD label shape
//  (labelId, labelActive, fields, a bare-array list response). The new
//  designer API uses designId / isActive / elements and wraps the list in
//  { designs: [...] }. To avoid touching every consumer, we map the new
//  shape back to the old field names on the way in:
//
//      designId   → labelId   (kept as labelId so foodItems.labelId,
//                              dropdowns and currentLabel all keep working)
//      isActive   → labelActive
//      elements   → fields     (designer elements, exposed under the old key)
//
//  designId is preserved too, so callers that prefer the new name still work.
// ──────────────────────────────────────────────────────────────

function mapDesign(d) {
  if (!d) return d;
  return {
    ...d,
    labelId:     d.designId,                       // legacy alias
    labelActive: d.isActive,                       // legacy alias
    fields:      d.elements || d.fields || [],     // legacy alias
  };
}

const state = {
  labels: [],
  currentLabel: null,
  loading: false,
  error: null
};

const getters = {
  activeLabels: (state) => state.labels.filter(l => l.labelActive === 1 || l.isActive === 1),
  getLabelById: (state) => (id) => state.labels.find(l => l.labelId === id || l.designId === id)
};

const mutations = {
  SET_LABELS(state, labels) { state.labels = labels; },
  SET_LABEL(state, label)   { state.currentLabel = label; },
  SET_LOADING(state, loading) { state.loading = loading; },
  SET_ERROR(state, error)     { state.error = error; },
  ADD_LABEL(state, label)     { state.labels.push(label); },
  UPDATE_LABEL(state, updated) {
    const i = state.labels.findIndex(l => l.labelId === updated.labelId);
    if (i !== -1) state.labels.splice(i, 1, updated);
  },
  DELETE_LABEL(state, labelId) {
    state.labels = state.labels.filter(l => l.labelId !== labelId);
  }
};

const actions = {
  async fetchLabels({ commit }, params = {}) {
    const baseURL = process.env.VUE_APP_API_URL || 'http://127.0.0.1/api';
    const userToken = localStorage.getItem('userToken');

    commit('SET_LOADING', true);
    commit('SET_ERROR', null);

    try {
      const response = await axios.get(`${baseURL}/designer`, {
        headers: { Authorization: `Bearer ${userToken}` },
        params
      });
      // New API: { designs: [...] }
      const designs = Array.isArray(response.data?.designs) ? response.data.designs : [];
      commit('SET_LABELS', designs.map(mapDesign));
    } catch (error) {
      console.error('Error fetching labels:', error);
      commit('SET_ERROR', error.message);
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },

  // Fetch one full design (label + elements) by designId.
  async fetchLabel({ commit }, id) {
    const baseURL = process.env.VUE_APP_API_URL || 'http://127.0.0.1/api';
    const userToken = localStorage.getItem('userToken');

    commit('SET_LOADING', true);
    commit('SET_ERROR', null);

    try {
      const response = await axios.get(`${baseURL}/designer/${id}`, {
        headers: { Authorization: `Bearer ${userToken}` }
      });
      const labelData = mapDesign(response.data);
      commit('SET_LABEL', labelData);
      return labelData;
    } catch (error) {
      console.error(`Error fetching label ${id}:`, error);
      commit('SET_ERROR', error.message);
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },

  async deleteLabel({ commit }, labelId) {
    const baseURL = process.env.VUE_APP_API_URL || 'http://127.0.0.1/api';
    const userToken = localStorage.getItem('userToken');

    commit('SET_LOADING', true);
    commit('SET_ERROR', null);

    try {
      await axios.delete(`${baseURL}/designer/${labelId}`, {
        headers: { Authorization: `Bearer ${userToken}` }
      });
      commit('DELETE_LABEL', labelId);
    } catch (error) {
      console.error('Error deleting label:', error);
      commit('SET_ERROR', error.message);
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },

  // Sets the active label and remembers the choice.
  async selectLabel({ dispatch }, labelId) {
    try {
      const label = await dispatch('fetchLabel', labelId);
      if (labelId) localStorage.setItem('selectedDesignId', labelId.toString());
      return label;
    } catch (error) {
      console.error('Error selecting label:', error);
      throw error;
    }
  },

  // On app start: load the list, then the previously-selected (or first) label.
  async initializeLabel({ dispatch, state }) {
    await dispatch('fetchLabels');

    if (state.labels && state.labels.length > 0) {
      const savedId = localStorage.getItem('selectedDesignId');
      let labelToLoad = null;

      if (savedId) {
        labelToLoad = state.labels.find(l => l.labelId === parseInt(savedId));
      }
      if (!labelToLoad) labelToLoad = state.labels[0];

      try {
        await dispatch('fetchLabel', labelToLoad.labelId);
      } catch (error) {
        console.warn('Could not load label:', error);
      }
    }
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
};